<template>
  <Dialog
    v-model:visible="displayModal"
    data-test-id="choose-items-dialog"
    :header="t(header)"
    :modal="true"
    :dismissable-mask="true"
    :style="{ width: '70vw' }"
    :breakpoints="{ '960px': '85vw', '640px': '95vw' }"
    @update:visible="handleClose"
  >
    <!-- Search bar -->
    <div class="search-container mb-3">
      <span class="p-input-icon-left w-full">
        <i class="fa-thin fa-magnifying-glass" />
        <InputText
          v-model="searchValue"
          data-test-id="search-item-input"
          :placeholder="t('templateBuilder.modals.typeFirstLetters')"
          class="w-full"
          @input="handleValueChange"
        />
      </span>
    </div>

    <!-- Labels -->
    <div class="flex labels-row mb-2">
      <div class="pane-label available-label">
        <span class="font-semibold">{{ t('templateBuilder.modals.availableItems') }}</span>
        <span class="count-badge">{{ availableList.length }}</span>
      </div>
      <div class="spacer" />
      <div class="pane-label chosen-label">
        <span class="font-semibold">{{ t('templateBuilder.modals.selectedItems') }}</span>
        <span class="count-badge selected">{{ selectedList.length }}</span>
      </div>
    </div>

    <!-- Main content -->
    <div class="flex pane-container">
      <!-- Available items -->
      <div class="pane available-pane">
        <div
          v-if="isLoading && availableList.length === 0"
          class="loading-state"
        >
          <i class="fa-solid fa-spinner fa-spin fa-2x" />
          <p>{{ t('templateBuilder.modals.loading') }}</p>
        </div>
        <div
          v-else-if="availableList.length === 0 && searchValue.length > 0"
          class="empty-state"
        >
          <i class="fa-thin fa-box-open fa-3x mb-2" />
          <p>{{ t('templateBuilder.modals.noItems.available') }}</p>
        </div>
        <div
          v-else-if="availableList.length === 0"
          class="empty-state"
        >
          <i class="fa-thin fa-magnifying-glass fa-3x mb-2" />
          <p>{{ t('templateBuilder.modals.searchHint') }}</p>
        </div>
        <div
          v-else
          ref="availableListRef"
          class="items-list"
          @scroll="handleAvailableScroll"
        >
          <template
            v-for="item in availableList"
            :key="item[fields.id]"
          >
            <div
              class="list-item"
              @click="handleAvailableItemClick(item)"
            >
              <div class="item-thumb">
                <img
                  v-if="getItemImage(item)"
                  :src="getItemImage(item)"
                  :alt="String(item[fields.label] || '')"
                >
                <i
                  v-else
                  class="fa-thin fa-image"
                />
              </div>
              <div class="item-info">
                <span class="item-id">#{{ item[fields.elementId] }}</span>
                <span
                  v-tooltip.top="item[fields.label]"
                  class="item-name"
                >{{ item[fields.label] }}</span>
                <span
                  v-if="enableVariants && hasCombinations(item)"
                  class="variants-count-badge"
                >
                  {{ t('templateBuilder.modals.variantsCount', { count: getVariantsCount(item) }) }}
                </span>
              </div>
              <i
                class="fa-solid item-action add"
                :class="'fa-plus'"
              />
            </div>
          </template>
          <div
            v-if="isLoadingMore"
            class="loading-more"
          >
            <i class="fa-solid fa-spinner fa-spin" />
            <span>{{ t('templateBuilder.modals.loadingMore') }}</span>
          </div>
          <div
            v-if="!hasMoreResults && availableList.length > 0"
            class="end-of-list"
          >
            <span>{{ t('templateBuilder.modals.endOfList') }}</span>
          </div>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="action-buttons">
        <div class="action-hint">
          <i class="fa-solid fa-arrow-right" />
        </div>
      </div>

      <!-- Selected items -->
      <div class="pane chosen-pane">
        <div
          v-if="selectedList.length === 0"
          class="empty-state"
        >
          <i class="fa-thin fa-clipboard-list fa-3x mb-2" />
          <p>{{ t('templateBuilder.modals.noItems.selected') }}</p>
        </div>
        <div
          v-else
          class="items-list"
        >
          <div
            v-for="item in selectedList"
            :key="getSelectedItemKey(item)"
            class="list-item selected"
            :class="{ 'variant-item': item._isVariant }"
            @click="item._isVariant ? removeVariant(item) : removeItem(item)"
          >
            <div class="item-thumb">
              <img
                v-if="getSelectedItemImage(item)"
                :src="getSelectedItemImage(item)"
                :alt="String(item._isVariant ? item._displayLabel : item[fields.label] || '')"
              >
              <i
                v-else
                class="fa-thin fa-image"
              />
            </div>
            <div class="item-info">
              <span class="item-id">
                #{{ item._isVariant ? item._variantElementId : item[fields.elementId] }}
              </span>
              <span
                v-tooltip.top="item._isVariant ? item._displayLabel : item[fields.label]"
                class="item-name"
              >{{ item._isVariant ? item._displayLabel : item[fields.label] }}</span>
              <span
                v-if="item._isVariant"
                class="variant-badge"
              >{{ t('templateBuilder.modals.variant') }}</span>
            </div>
            <i class="fa-solid fa-minus item-action remove" />
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-content-between align-items-center w-full">
        <span class="selection-summary">
          {{ t('templateBuilder.modals.itemsSelected', { count: selectedList.length }) }}
        </span>
        <div class="flex gap-2">
          <Button
            data-test-id="choose-items-cancel-button"
            :label="t('templateBuilder.modals.cancel')"
            class="p-button-secondary"
            @click="handleClose"
          />
          <Button
            data-test-id="choose-items-validate-button"
            :label="t('templateBuilder.modals.validate')"
            class="validate-btn"
            @click="handleChooseItems"
          >
            <i class="fa-solid fa-check mr-2" />
            {{ t('templateBuilder.modals.validate') }}
          </Button>
        </div>
      </div>
    </template>
  </Dialog>
  <ChooseVariantsModal
    :display-dialog="variantModalVisible"
    :product="variantModalProduct"
    :variants="variantModalVariants"
    :is-loading="isVariantModalLoading"
    :is-loading-more="isVariantModalLoadingMore"
    :has-more-results="variantModalHasMoreResults"
    :fields="fields"
    :initial-product-selected="isVariantModalProductSelected"
    :initial-selected-variant-ids="variantModalInitialSelectedVariants"
    @on-close-dialog="handleCloseVariantModal"
    @on-validate-selection="handleVariantModalValidate"
    @on-search-change="handleVariantSearchChange"
    @on-load-more="handleVariantLoadMore"
  />
</template>

<script lang="ts">
import {
  computed, ComputedRef, defineComponent, onMounted, PropType, ref, Ref, watch,
} from 'vue';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import { useI18n } from 'vue-i18n';
import { ListSettings } from '@/composables/GraphQL';
import { getAuthHeaders } from '@/composables/nestApi';
import { SpmTableFilter, StringMap } from '@/types';
import { showToastError } from '@/helpers';
import { OperatorType } from '@/types/generated-types/graphql';
import ChooseVariantsModal from '@/components/modals/ChooseVariantsModal.vue';

const PAGE_SIZE = 50;
const VARIANTS_PAGE_SIZE = 10;
const PRODUCT_IMAGE_FIELD = 'image_link';

const FULLTEXT_CONFIG: Record<string, string> = {
  ShopsProducts: 'id_product',
  ShopsProductsCategoriesList: 'id_category',
  ShopsManufacturers: 'id_manufacturer',
};

// Same resolvers as *List, minus the unused `total` COUNT over the fulltext match.
const SELECTION_QUERY: Record<string, string> = {
  ShopsProducts: 'ShopsProductsSelection',
  ShopsProductsCategoriesList: 'ShopsProductsCategoriesSelection',
  ShopsManufacturers: 'ShopsManufacturersSelection',
};

const COMBINATION_FIELDS = 'id_shop_product_attribute id_shop_product id_combination combination_name reference price price_discount quantity image_link';
const COMBINATION_COUNT_FIELD = 'combinations_count';

interface CombinationItem {
  id_shop_product_attribute: number;
  id_shop_product: number;
  id_combination: number;
  combination_name: string;
  reference: string;
  price: number;
  price_discount: number;
  quantity: number;
  image_link?: string;
}

interface VariantModalSelectionPayload {
  selectProduct: boolean;
  variants: CombinationItem[];
}

export default defineComponent({
  name: 'ChooseItemsModal',

  components: {
    Dialog,
    InputText,
    Button,
    ChooseVariantsModal,
  },

  props: {
    displayDialog: {
      type: Boolean,
      required: true,
    },

    header: {
      type: String,
      required: true,
    },

    name: {
      type: String,
      required: true,
    },

    fields: {
      type: Object as PropType<StringMap>,
      required: true,
    },

    items: {
      type: String,
      required: false,
      default: '',
    },


    defaultFilters: {
      type: Array as PropType<SpmTableFilter[]>,
    },

    enableVariants: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: {
    'on-close-dialog': null,
    'on-choose-items': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const displayModal: ComputedRef<boolean> = computed(() => props.displayDialog);
    const searchValue: Ref<string> = ref('');
    const availableListRef = ref<HTMLElement | null>(null);

    const isLoading = ref(false);
    const isLoadingMore = ref(false);
    const hasMoreResults = ref(true);
    const currentOffset = ref(0);
    let searchTimer: ReturnType<typeof setTimeout> | null = null;
    let searchRequestId = 0;

    const settings: Ref<ListSettings> = ref({
      limit: PAGE_SIZE,
      offset: 0,
      order: [],
      filter: [],
    });

    const availableList: Ref<StringMap[]> = ref([]);
    const selectedList: Ref<StringMap[]> = ref([]);
    const variantModalVisible = ref(false);
    const variantModalProduct: Ref<StringMap | null> = ref(null);
    const variantModalVariants: Ref<CombinationItem[]> = ref([]);
    const variantModalInitialSelectedVariants: Ref<CombinationItem[]> = ref([]);
    const isVariantModalProductSelected = ref(false);
    const isVariantModalLoading = ref(false);
    const isVariantModalLoadingMore = ref(false);
    const variantModalHasMoreResults = ref(true);
    const variantModalOffset = ref(0);
    const variantModalSearchValue = ref('');

    let currentAbortController: AbortController | null = null;
    let variantAbortController: AbortController | null = null;
    let hydrationAbortController: AbortController | null = null;
    let variantRequestId = 0;
    let variantSearchTimer: ReturnType<typeof setTimeout> | null = null;

    const fetchDataWithSignal = async (
      fetchSettings: ListSettings,
      signal: AbortSignal,
      includeCombinations = false,
      includeCombinationCount = props.enableVariants,
    ): Promise<StringMap[]> => {
      const fname = SELECTION_QUERY[props.name] ?? `${props.name}List`;
      const extraProductFields = props.name === 'ShopsProducts' ? ` ${PRODUCT_IMAGE_FIELD}` : '';
      const fieldsStr = `${props.fields.id} ${props.fields.elementId} ${props.fields.label}${extraProductFields}`;
      const combinationsFragment = includeCombinations ? ` combinations { ${COMBINATION_FIELDS} }` : '';
      const combinationsCountFragment = includeCombinationCount ? ` ${COMBINATION_COUNT_FIELD}` : '';

      const query = `query ($limit: Int!, $offset: Int!, $order: [OrderParameters], $filter: [FilterParameters]) {
        ${fname} (params: {limit: $limit, offset: $offset, order: $order, filter: $filter}) {
          items { ${fieldsStr}${combinationsCountFragment}${combinationsFragment} }
        }
      }`;

      try {
        const response = await fetch('/api/graphql', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            query,
            variables: fetchSettings,
          }),
          signal,
        });

        const result = await response.json();
        return result.data?.[fname]?.items ?? [];
      } catch (error: any) {
        if (error.name === 'AbortError') {
          return [];
        }
        showToastError('GENERIC_ERROR');
        return [];
      }
    };

    const fetchData = async (): Promise<StringMap[]> => fetchDataWithSignal(
      settings.value,
      currentAbortController?.signal ?? new AbortController().signal,
    );

    const filterOutSelected = (items: StringMap[]): StringMap[] => items.filter(
      (item) => !selectedList.value.some(
        (selected) => !selected._isVariant && selected[props.fields.id] === item[props.fields.id],
      ),
    );

    const getSelectedItemKey = (item: StringMap): string => {
      if (item._isVariant) {
        return `v_${item._variantId}`;
      }
      return `p_${item[props.fields.id]}`;
    };

    const hasCombinations = (item: StringMap): boolean => {
      const combos = item.combinations as unknown as CombinationItem[] | undefined;
      if (Array.isArray(combos)) {
        return combos.length > 0;
      }
      return Number(item[COMBINATION_COUNT_FIELD] ?? 0) > 0;
    };

    const getVariantsCount = (item: StringMap): number => {
      const combos = item.combinations as unknown as CombinationItem[] | undefined;
      if (Array.isArray(combos)) {
        return combos.length;
      }
      return Number(item[COMBINATION_COUNT_FIELD] ?? 0);
    };

    const getItemImage = (item: StringMap): string => String(
      item.image_link
      ?? item.image_url
      ?? '',
    ).trim();

    const getSelectedItemImage = (item: StringMap): string => String(
      item._isVariant
        ? (item._variantImageLink ?? '')
        : (item.image_link ?? item.image_url ?? ''),
    ).trim();

    const ensureProductInAvailableList = (product: StringMap) => {
      if (!availableList.value.some((el) => String(el[props.fields.id]) === String(product[props.fields.id]))) {
        availableList.value.unshift(product);
      }
    };

    const fetchProductCombinations = async (product: StringMap): Promise<CombinationItem[]> => {
      if (variantAbortController) {
        variantAbortController.abort();
      }
      const fetchSettings: ListSettings = {
        limit: 1,
        offset: 0,
        order: [],
        filter: [
          { field: props.fields.id, value: String(product[props.fields.id]), operator: OperatorType.Equals },
          ...(props.defaultFilters ?? []),
          { field: 'productsAttributes', value: 'true', operator: OperatorType.Equals },
          { field: 'combinationsLimit', value: String(VARIANTS_PAGE_SIZE), operator: OperatorType.Equals },
          { field: 'combinationsOffset', value: String(variantModalOffset.value), operator: OperatorType.Equals },
        ],
      };
      if (variantModalSearchValue.value.trim() !== '') {
        fetchSettings.filter.push({
          field: 'combinationsSearch',
          value: variantModalSearchValue.value.trim(),
          operator: OperatorType.Contains,
        });
      }

      variantAbortController = new AbortController();
      const products = await fetchDataWithSignal(
        fetchSettings,
        variantAbortController.signal,
        true,
        false,
      );

      const [productWithVariants] = products;
      const variants = (productWithVariants?.combinations || []) as unknown as CombinationItem[];
      return variants;
    };

    const loadVariantModalPage = async (reset = false) => {
      if (!variantModalProduct.value) return;
      if (reset) {
        variantModalOffset.value = 0;
        variantModalHasMoreResults.value = true;
        variantModalVariants.value = [];
      }
      if (!variantModalHasMoreResults.value && !reset) return;
      if (reset) {
        isVariantModalLoading.value = true;
      } else {
        isVariantModalLoadingMore.value = true;
      }

      variantRequestId += 1;
      const requestId = variantRequestId;
      try {
        const variants = await fetchProductCombinations(variantModalProduct.value);
        if (requestId !== variantRequestId) {
          return;
        }
        if (reset) {
          variantModalVariants.value = variants;
        } else {
          const existing = new Set(variantModalVariants.value.map((v) => String(v.id_shop_product_attribute)));
          const nextVariants = variants.filter((v) => !existing.has(String(v.id_shop_product_attribute)));
          variantModalVariants.value = [...variantModalVariants.value, ...nextVariants];
        }
        variantModalHasMoreResults.value = variants.length === VARIANTS_PAGE_SIZE;
        variantModalOffset.value += variants.length;
      } finally {
        if (requestId === variantRequestId) {
          isVariantModalLoading.value = false;
          isVariantModalLoadingMore.value = false;
        }
      }
    };

    const openVariantModal = async (product: StringMap) => {
      const productId = String(product[props.fields.id]);
      variantModalProduct.value = product;
      variantModalVariants.value = [];
      variantModalSearchValue.value = '';
      variantModalOffset.value = 0;
      variantModalHasMoreResults.value = true;
      variantModalInitialSelectedVariants.value = selectedList.value
        .filter(
          (selected) => selected._isVariant
            && String(selected._parentProductId ?? selected[props.fields.id]) === productId,
        )
        .map((selected) => ({
          id_shop_product_attribute: Number(selected._variantId),
          id_shop_product: Number(product[props.fields.id]),
          id_combination: Number(selected._variantElementId),
          combination_name: String(selected._combinationName ?? ''),
          reference: '',
          price: 0,
          price_discount: 0,
          quantity: 0,
          image_link: String(selected._variantImageLink ?? ''),
        }));
      isVariantModalProductSelected.value = selectedList.value.some(
        (selected) => !selected._isVariant && String(selected[props.fields.id]) === productId,
      );
      variantModalVisible.value = true;
      await loadVariantModalPage(true);
    };

    const handleAvailableItemClick = async (item: StringMap) => {
      if (props.enableVariants && hasCombinations(item)) {
        await openVariantModal(item);
        return;
      }
      selectItem(item);
    };

    const handleCloseVariantModal = () => {
      if (variantSearchTimer) {
        clearTimeout(variantSearchTimer);
        variantSearchTimer = null;
      }
      variantRequestId += 1;
      if (variantAbortController) {
        variantAbortController.abort();
        variantAbortController = null;
      }
      isVariantModalLoading.value = false;
      isVariantModalLoadingMore.value = false;
      variantModalVisible.value = false;
    };

    const handleVariantSearchChange = (value: string) => {
      variantModalSearchValue.value = value;
      if (variantSearchTimer) {
        clearTimeout(variantSearchTimer);
      }
      variantSearchTimer = setTimeout(() => {
        loadVariantModalPage(true);
      }, 500);
    };

    const handleVariantLoadMore = () => {
      if (isVariantModalLoading.value || isVariantModalLoadingMore.value || !variantModalHasMoreResults.value) {
        return;
      }
      loadVariantModalPage(false);
    };

    const clearProductSelections = (product: StringMap) => {
      const productId = String(product[props.fields.id]);
      selectedList.value = selectedList.value.filter((selected: StringMap) => {
        if (!selected._isVariant) {
          return String(selected[props.fields.id]) !== productId;
        }
        return String(selected._parentProductId ?? selected[props.fields.id]) !== productId;
      });
    };

    const handleVariantModalValidate = (payload: VariantModalSelectionPayload) => {
      if (!variantModalProduct.value) {
        handleCloseVariantModal();
        return;
      }

      const product = variantModalProduct.value;
      clearProductSelections(product);

      if (payload.selectProduct) {
        selectItem(product);
        handleCloseVariantModal();
        return;
      }

      payload.variants.forEach((variant) => selectVariant(product, variant));
      ensureProductInAvailableList(product);
      handleCloseVariantModal();
    };

    const selectItem = (element: StringMap) => {
      const productId = String(element[props.fields.id]);

      // If full product is selected, remove its selected variants.
      selectedList.value = selectedList.value.filter((selected: StringMap) => {
        if (!selected._isVariant) {
          return String(selected[props.fields.id]) !== productId;
        }
        return String(selected._parentProductId ?? selected[props.fields.id]) !== productId;
      });

      selectedList.value.unshift(element);
      availableList.value = availableList.value.filter(
        (el: StringMap) => el[props.fields.id] !== element[props.fields.id],
      );
    };

    const selectVariant = (parentProduct: StringMap, variant: CombinationItem) => {
      const variantLabel = variant.combination_name || variant.reference || `#${variant.id_combination}`;
      selectedList.value.unshift({
        [props.fields.id]: parentProduct[props.fields.id],
        [props.fields.elementId]: parentProduct[props.fields.elementId],
        [props.fields.label]: parentProduct[props.fields.label],
        _isVariant: 'true',
        _variantId: String(variant.id_shop_product_attribute),
        _variantElementId: String(variant.id_combination),
        _combinationName: variantLabel,
        _displayLabel: `${parentProduct[props.fields.label]} — ${variantLabel}`,
        _parentProductId: parentProduct[props.fields.id],
        _variantImageLink: variant.image_link ?? '',
      });
    };

    const removeItem = (element: StringMap) => {
      selectedList.value = selectedList.value.filter(
        (el: StringMap) => el[props.fields.id] !== element[props.fields.id] || el._isVariant,
      );
      if (!availableList.value.some((el) => el[props.fields.id] === element[props.fields.id])) {
        availableList.value.push(element);
      }
    };

    const removeVariant = (element: StringMap) => {
      selectedList.value = selectedList.value.filter(
        (el: StringMap) => !(el._isVariant && el._variantId === element._variantId),
      );
    };

    const handleValueChange = async () => {
      if (searchTimer) clearTimeout(searchTimer);

      if (currentAbortController) {
        currentAbortController.abort();
      }

      searchRequestId += 1;
      const currentRequestId = searchRequestId;

      const searchTerm = searchValue.value.trim();
      if (!searchTerm) {
        availableList.value = [];
        isLoading.value = false;
        return;
      }

      searchTimer = setTimeout(async () => {
        if (currentRequestId !== searchRequestId) return;

        currentAbortController = new AbortController();
        const { signal } = currentAbortController;

        isLoading.value = true;
        currentOffset.value = 0;
        hasMoreResults.value = true;
        availableList.value = [];

        const isFulltextType = props.name in FULLTEXT_CONFIG;
        const isSingleWord = !searchTerm.includes(' ');

        settings.value.limit = PAGE_SIZE;
        settings.value.offset = 0;

        const searchOperator = isFulltextType ? OperatorType.Against : OperatorType.Contains;
        const variantsFilter = props.enableVariants
          ? [{ field: 'productsAttributesCount', value: 'true', operator: OperatorType.Equals }]
          : [];

        settings.value.filter = [
          { field: props.fields.label, value: searchTerm, operator: searchOperator },
          ...(props.defaultFilters ?? []),
          ...variantsFilter,
        ];

        let results: StringMap[] = [];

        if (isFulltextType && isSingleWord) {
          const idField = FULLTEXT_CONFIG[props.name];

          const idSettings: ListSettings = {
            limit: 1,
            offset: 0,
            order: [],
            filter: [
              { field: idField, value: searchTerm, operator: OperatorType.Equals },
              ...(props.defaultFilters ?? []),
              ...variantsFilter,
            ],
          };

          const [nameResults, idResults] = await Promise.all([
            fetchDataWithSignal(settings.value, signal, false, props.enableVariants),
            fetchDataWithSignal(idSettings, signal, false, props.enableVariants),
          ]);

          if (currentRequestId !== searchRequestId) return;

          results = [...idResults];
          nameResults.forEach((item) => {
            if (!results.some((r) => r[props.fields.id] === item[props.fields.id])) {
              results.push(item);
            }
          });
        } else {
          results = await fetchDataWithSignal(settings.value, signal, false, props.enableVariants);
          if (currentRequestId !== searchRequestId) return;
        }

        availableList.value = filterOutSelected(results);
        hasMoreResults.value = results.length >= PAGE_SIZE;
        currentOffset.value = PAGE_SIZE;
        isLoading.value = false;
      }, 500);
    };

    const loadMore = async () => {
      if (isLoadingMore.value || !hasMoreResults.value || !currentAbortController) return;

      searchRequestId += 1;
      const currentRequestId = searchRequestId;

      isLoadingMore.value = true;
      settings.value.offset = currentOffset.value;

      const results = await fetchDataWithSignal(
        settings.value,
        currentAbortController.signal,
        false,
        props.enableVariants,
      );

      if (currentRequestId !== searchRequestId) {
        isLoadingMore.value = false;
        return;
      }

      if (results.length > 0) {
        const newItems = filterOutSelected(results);
        availableList.value = [...availableList.value, ...newItems];
        hasMoreResults.value = results.length === PAGE_SIZE;
        currentOffset.value += PAGE_SIZE;
      } else {
        hasMoreResults.value = false;
      }
      isLoadingMore.value = false;
    };

    const handleAvailableScroll = (event: Event) => {
      const target = event.target as HTMLElement;
      const { scrollTop, scrollHeight, clientHeight } = target;

      if (scrollHeight - scrollTop - clientHeight < 100) {
        loadMore();
      }
    };

    const handleClose = () => {
      context.emit('on-close-dialog');
    };

    const handleChooseItems = () => {
      context.emit('on-choose-items', selectedList.value);
      handleClose();
    };

    const hydrateSelectedItems = async () => {
      if (hydrationAbortController) {
        hydrationAbortController.abort();
      }
      const abortCtrl = new AbortController();
      hydrationAbortController = abortCtrl;
      selectedList.value = [];

      if (!props.items || props.items.trim() === '') return;

      const rawTokens = props.items.split(',').map((t) => t.trim()).filter((t) => t !== '');
      const productIds: string[] = [];
      const combinationsByProduct = new Map<string, number[]>();

      rawTokens.forEach((token) => {
        const match = token.match(/^([^[\]]+)(?:\[([0-9;]+)\])?$/);
        if (!match) return;
        productIds.push(match[1]);
        if (match[2]) {
          const combos = match[2]
            .split(';')
            .map((id) => parseInt(id, 10))
            .filter((id) => !Number.isNaN(id) && id > 0);
          if (combos.length) {
            combinationsByProduct.set(match[1], combos);
          }
        }
      });

      if (!productIds.length) return;

      const needCombinations = combinationsByProduct.size > 0 && props.enableVariants;
      const hydrationFilters: SpmTableFilter[] = [
        { field: props.fields.id, value: productIds.join(','), operator: OperatorType.In },
      ];
      if (props.defaultFilters?.length) {
        hydrationFilters.push(...props.defaultFilters);
      }
      if (needCombinations && props.name === 'ShopsProducts') {
        hydrationFilters.push(
          { field: 'productsAttributes', value: 'true', operator: OperatorType.Equals },
          { field: 'combinationsLimit', value: '1000', operator: OperatorType.Equals },
          { field: 'combinationsOffset', value: '0', operator: OperatorType.Equals },
        );
      }
      settings.value.filter = hydrationFilters;
      settings.value.limit = 1000;

      const products = await fetchDataWithSignal(
        settings.value,
        abortCtrl.signal,
        needCombinations,
        props.enableVariants,
      );
      settings.value.limit = PAGE_SIZE;
      // A newer reload owns the list.
      if (abortCtrl.signal.aborted) return;

      products.forEach((product: StringMap) => {
        const productId = String(product[props.fields.id]);
        const combos = combinationsByProduct.get(productId);

        if (combos && combos.length > 0 && props.enableVariants) {
          const combinations = (product.combinations || []) as unknown as CombinationItem[];
          const comboSet = new Set(combos);
          combinations.forEach((variant) => {
            if (comboSet.has(variant.id_combination)) {
              selectVariant(product, variant);
            }
          });
        } else {
          selectedList.value.push(product);
        }
      });
    };

    watch(
      () => props.displayDialog,
      async (isOpen) => {
        if (isOpen) {
          await hydrateSelectedItems();
        }
      },
    );

    onMounted(async () => {
      await hydrateSelectedItems();
    });

    return {
      t,
      searchValue,
      availableList,
      selectedList,
      displayModal,
      availableListRef,
      variantModalVisible,
      variantModalProduct,
      variantModalVariants,
      variantModalInitialSelectedVariants,
      isVariantModalProductSelected,
      isVariantModalLoading,
      isVariantModalLoadingMore,
      variantModalHasMoreResults,
      selectItem,
      selectVariant,
      removeItem,
      removeVariant,
      handleAvailableItemClick,
      handleCloseVariantModal,
      handleVariantModalValidate,
      handleVariantSearchChange,
      handleVariantLoadMore,
      hasCombinations,
      getVariantsCount,
      getItemImage,
      getSelectedItemImage,
      getSelectedItemKey,
      handleClose,
      handleValueChange,
      handleChooseItems,
      handleAvailableScroll,
      isLoading,
      isLoadingMore,
      hasMoreResults,
    };
  },
});
</script>

<style scoped lang="scss">
@import '@/assets/scss/utilities/_variables.scss';

.search-container {
  .p-inputtext {
    padding-left: 2.5rem;
  }
}

.labels-row {
  .pane-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &.available-label {
      width: 45%;
    }

    &.chosen-label {
      width: 45%;
    }
  }

  .spacer {
    width: 10%;
  }

  .count-badge {
    background: $extra-light-grey;
    color: $tuna;
    padding: 0.15rem 0.5rem;
    border-radius: 1rem;
    font-size: 0.8rem;
    font-weight: 600;

    &.selected {
      background: lighten($brand-color-primary, 35%);
      color: darken($brand-color-primary, 20%);
    }
  }
}

.pane-container {
  height: 350px;
  gap: 0;
}

.pane {
  height: 100%;
  border: 1px solid $solitude;
  border-radius: 6px;
  overflow: hidden;
  background: $white;

  &.available-pane {
    width: 45%;
  }

  &.chosen-pane {
    width: 45%;
  }
}

.action-buttons {
  width: 10%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .action-hint {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $extra-light-grey;
    border-radius: 50%;
    color: $mid-grey;
  }
}

.items-list {
  height: 100%;
  overflow-y: auto;
  padding: 0.5rem;
}

.list-item {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0.25rem;

  &:hover {
    background: $extra-light-grey;

    .item-action {
      opacity: 1;
    }
  }

  &.selected {
    background: lighten($brand-color-primary, 42%);

    &:hover {
      background: lighten($brand-color-primary, 38%);
    }
  }

  &.variant-item {
    padding-left: 2rem;

    .item-id {
      font-style: italic;
    }
  }

  .item-thumb {
    width: 34px;
    height: 34px;
    border-radius: 6px;
    background: $extra-light-grey;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
    color: $mid-grey;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  .item-info {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1;

    .item-id {
      font-size: 0.75rem;
      color: $mid-grey;
      font-weight: 500;
    }

    .item-name {
      font-size: 0.875rem;
      color: $tuna;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .variants-count-badge {
      margin-top: 0.2rem;
      font-size: 0.68rem;
      width: fit-content;
      background: lighten($brand-color-primary, 36%);
      color: darken($brand-color-primary, 16%);
      border-radius: 999px;
      padding: 0.08rem 0.45rem;
      font-weight: 600;
    }
  }

  .item-action {
    opacity: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &.add {
      background: $brand-color-primary;
      color: $white;
    }

    &.remove {
      background: #dc3545;
      color: $white;
    }
  }
}

.variant-badge {
  font-size: 0.65rem;
  background: lighten($brand-color-primary, 35%);
  color: darken($brand-color-primary, 15%);
  padding: 0.1rem 0.4rem;
  border-radius: 3px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  width: fit-content;
}

.empty-state,
.loading-state {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: $mid-grey;
  text-align: center;
  padding: 1rem;

  i {
    color: $mid-grey;
    margin-bottom: 0.5rem;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
  }
}

.loading-more,
.end-of-list {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  color: $mid-grey;
  font-size: 0.85rem;
}

.selection-summary {
  color: $mid-grey;
  font-size: 0.9rem;
}

.validate-btn {
  background: $brand-color-primary;
  border-color: $brand-color-primary;

  &:hover {
    background: $successButtonHoverBg;
    border-color: $successButtonHoverBg;
  }
}
</style>
